import React, { useState, useMemo } from 'react';
import {
  Check,
  ChevronDown,
  Search,
  Server,
  Cloud,
  Cpu,
  RefreshCw,
  AlertCircle,
  Star,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { Button } from './Button';
import { Badge } from './Badge';
import { useAiModels } from '../helpers/useAiModels';
import { Spinner } from './Spinner';
import styles from './ModelSelector.module.css';

export type ModelSelection = {
  provider: string;
  model: string;
  isLocal: boolean;
};

interface ModelSelectorProps {
  value?: ModelSelection;
  onChange: (selection: ModelSelection) => void;
  className?: string;
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({ value, onChange, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'cloud' | 'local'>('cloud');
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: modelsData,
    isLoading,
    isRefetching,
    refetch,
    error,
  } = useAiModels({
    enabled: isOpen,
    probeLocal: isOpen && (value?.isLocal || activeTab === 'local'),
  });

  // Quick toggle between cloud and local
  const handleQuickToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!modelsData) return;

    const newIsLocal = !value?.isLocal;

    if (newIsLocal) {
      // Switch to local - use first available local model or keep current if it's local
      const localModels = modelsData.localModels || [];
      if (localModels.length > 0) {
        onChange({
          provider: 'ollama_local',
          model: localModels[0].model,
          isLocal: true,
        });
      }
    } else {
      // Switch to cloud - use first available cloud model or keep current if it's cloud
      const cloudModels = modelsData.cloudModels || [];
      if (cloudModels.length > 0) {
        onChange({
          provider: cloudModels[0].provider || 'ollama_cloud',
          model: cloudModels[0].model,
          isLocal: false,
        });
      }
    }
  };

  const cloudModels = useMemo(() => {
    if (!modelsData?.cloudModels) return [];
    const safeQuery = typeof searchQuery === 'string' ? searchQuery.toLowerCase() : '';
    return modelsData.cloudModels.filter(
      (m) =>
        (typeof m.name === 'string' && m.name.toLowerCase().includes(safeQuery)) ||
        (typeof m.model === 'string' && m.model.toLowerCase().includes(safeQuery)),
    );
  }, [modelsData, searchQuery]);

  const localModels = useMemo(() => {
    if (!modelsData?.localModels) return [];
    const safeQuery = typeof searchQuery === 'string' ? searchQuery.toLowerCase() : '';
    return modelsData.localModels.filter(
      (m) =>
        (typeof m.name === 'string' && m.name.toLowerCase().includes(safeQuery)) ||
        (typeof m.model === 'string' && m.model.toLowerCase().includes(safeQuery)),
    );
  }, [modelsData, searchQuery]);

  const customModels = useMemo(() => {
    if (!modelsData?.customModels) return [];
    const safeQuery = typeof searchQuery === 'string' ? searchQuery.toLowerCase() : '';
    return modelsData.customModels.filter(
      (m) =>
        (typeof m.name === 'string' && m.name.toLowerCase().includes(safeQuery)) ||
        (typeof m.model === 'string' && m.model.toLowerCase().includes(safeQuery)),
    );
  }, [modelsData, searchQuery]);

  const handleSelect = (selection: ModelSelection) => {
    onChange(selection);
    setIsOpen(false);
  };

  // Check if we have models available
  const hasLocalModels = (modelsData?.localModels?.length ?? 0) > 0;
  const hasCloudModels = (modelsData?.cloudModels?.length ?? 0) > 0;
  const canToggle = hasLocalModels && hasCloudModels;

  const currentLabel = useMemo(() => {
    if (isLoading) return 'Loading...';
    if (!value) {
      // If no model selected, show a helpful default
      if (hasCloudModels) return 'Select Model';
      if (hasLocalModels) return 'Select Local';
      return 'No Models';
    }

    if (value.isLocal) {
      if (!modelsData?.localModels?.length) return value.model;
      // Try to find nice name from local models
      const found = modelsData?.localModels.find((m) => m.model === value.model);
      return found?.name || value.model;
    } else {
      if (!modelsData?.cloudModels?.length && !modelsData?.customModels?.length) return value.model;
      // Try to find nice name from cloud models, then custom models
      const found = modelsData?.cloudModels.find(
        (m) => m.model === value.model && m.provider === value.provider,
      );
      if (found) return found.name;
      const customFound = modelsData?.customModels?.find((m) => m.model === value.model);
      return customFound?.name || value.model;
    }
  }, [value, modelsData, isLoading, hasCloudModels, hasLocalModels]);

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    refetch();
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Refresh models when opening the selector so newly discovered local models appear
      try {
        refetch();
      } catch (e) {
        // ignore
      }
    }
  };

  // Determine tooltip message
  const getToggleTooltip = () => {
    if (isLoading) return 'Loading models...';
    if (!hasCloudModels && !hasLocalModels) return 'No models available - check AI settings';
    if (!hasLocalModels) return 'Using Cloud AI (no local models available)';
    if (!hasCloudModels) return 'Using Local LLM (no cloud models available)';
    return value?.isLocal
      ? 'Using Local LLM - Click to switch to Cloud'
      : 'Using Cloud AI - Click to switch to Local';
  };

  return (
    <div className={styles.selectorWrapper}>
      {/* Quick toggle button - always visible for switching cloud/local */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={handleQuickToggle}
            className={`${styles.quickToggle} ${isLoading ? styles.loading : ''}`}
            disabled={!canToggle || isLoading}
          >
            {isLoading ? (
              <RefreshCw size={16} className={styles.spinning} />
            ) : value?.isLocal ? (
              <Cpu size={16} className={styles.localIcon} />
            ) : (
              <Cloud size={16} className={styles.cloudIcon} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{getToggleTooltip()}</TooltipContent>
      </Tooltip>

      {/* Model dropdown */}
      <Popover open={isOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="outline" className={`${styles.trigger} ${className || ''}`}>
            <span className={styles.triggerLabel}>{currentLabel}</span>
            <ChevronDown size={12} className={styles.chevron} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className={styles.content} align="start" removeBackgroundAndPadding>
          <div className={styles.popoverContainer}>
            {/* Tabs */}
            <div className={styles.tabs}>
              <button
                className={`${styles.tab} ${activeTab === 'cloud' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('cloud')}
              >
                <Cloud size={14} />
                Cloud
              </button>
              <button
                className={`${styles.tab} ${activeTab === 'local' ? styles.activeTab : ''}`}
                onClick={() => setActiveTab('local')}
              >
                <Server size={14} />
                Local
              </button>
            </div>

            {/* Search */}
            <div className={styles.searchWrapper}>
              <Search size={14} className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                placeholder="Search models..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleRefresh}
                title="Refresh models"
                disabled={isRefetching || isLoading}
                className={isRefetching ? styles.spinning : ''}
              >
                <RefreshCw size={14} />
              </Button>
            </div>

            {/* List */}
            <div className={styles.list}>
              {(() => {
                if (isLoading) {
                  return (
                    <div className={styles.loading}>
                      <Spinner size="sm" />
                      <span>Loading models...</span>
                    </div>
                  );
                }

                if (error) {
                  return (
                    <div className={styles.error}>
                      <AlertCircle size={16} />
                      <span>Failed to load models</span>
                    </div>
                  );
                }

                if (activeTab === 'cloud') {
                  const hasAny = cloudModels.length > 0 || customModels.length > 0;
                  return hasAny ? (
                    <>
                      {cloudModels.map((m) => (
                        <button
                          key={`${m.provider}-${m.model}`}
                          className={`${styles.item} ${
                            value?.model === m.model && !value.isLocal ? styles.selectedItem : ''
                          }`}
                          onClick={() =>
                            handleSelect({
                              provider: m.provider || 'openai',
                              model: m.model,
                              isLocal: false,
                            })
                          }
                        >
                          <div className={styles.itemInfo}>
                            <span className={styles.itemName}>{m.name}</span>
                            {m.provider && m.provider !== 'ollama_cloud' && (
                              <span className={styles.itemProvider}>{m.provider}</span>
                            )}
                          </div>
                          <div className={styles.itemTags}>
                            {m.tags?.map((t) => (
                              <Badge key={t} variant="secondary" className={styles.badge}>
                                {t}
                              </Badge>
                            ))}
                            {value?.model === m.model && !value.isLocal && (
                              <Check size={14} className={styles.check} />
                            )}
                          </div>
                        </button>
                      ))}

                      {/* Custom models section */}
                      {customModels.length > 0 && (
                        <>
                          {cloudModels.length > 0 && (
                            <div className={styles.endpointInfo}>
                              <Star size={12} />
                              <span className={styles.endpointLabel}>Your Custom Models</span>
                            </div>
                          )}
                          {customModels.map((m) => (
                            <button
                              key={`custom-${m.provider}-${m.model}`}
                              className={`${styles.item} ${
                                value?.model === m.model && !value.isLocal
                                  ? styles.selectedItem
                                  : ''
                              }`}
                              onClick={() =>
                                handleSelect({
                                  provider: m.provider || 'openai',
                                  model: m.model,
                                  isLocal: false,
                                })
                              }
                            >
                              <div className={styles.itemInfo}>
                                <span className={styles.itemName}>{m.name}</span>
                                <span className={styles.itemProvider}>{m.provider}</span>
                              </div>
                              <div className={styles.itemTags}>
                                <Badge variant="outline" className={styles.badge}>
                                  Custom
                                </Badge>
                                {value?.model === m.model && !value.isLocal && (
                                  <Check size={14} className={styles.check} />
                                )}
                              </div>
                            </button>
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    <div className={styles.empty}>
                      {`No cloud models found matching "${searchQuery}"`}
                    </div>
                  );
                }

                if (activeTab === 'local') {
                  return (
                    <>
                      {modelsData?.localEndpoint && (
                        <div className={styles.endpointInfo}>
                          <div className={styles.endpointLabel}>Connected to:</div>
                          <div className={styles.endpointUrl}>{modelsData.localEndpoint}</div>
                        </div>
                      )}

                      {modelsData?.error && (
                        <div className={styles.localError}>
                          <AlertCircle size={14} />
                          <span>{modelsData.error}</span>
                        </div>
                      )}

                      {localModels.length > 0 ? (
                        localModels.map((m) => (
                          <button
                            key={m.model}
                            className={`${styles.item} ${
                              value?.model === m.model && value.isLocal ? styles.selectedItem : ''
                            }`}
                            onClick={() =>
                              handleSelect({
                                provider: 'ollama_local',
                                model: m.model,
                                isLocal: true,
                              })
                            }
                          >
                            <div className={styles.itemInfo}>
                              <span className={styles.itemName}>{m.name}</span>
                              {m.size && <span className={styles.itemProvider}>{m.size}</span>}
                            </div>
                            <div className={styles.itemTags}>
                              <Badge variant="outline" className={styles.badge}>
                                Local
                              </Badge>
                              {value?.model === m.model && value.isLocal && (
                                <Check size={14} className={styles.check} />
                              )}
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className={styles.empty}>
                          {modelsData?.localEnabled
                            ? isRefetching
                              ? 'Refreshing...'
                              : `No local models found matching "${searchQuery}"`
                            : 'Local LLM is not enabled in settings'}
                        </div>
                      )}
                    </>
                  );
                }

                return null;
              })()}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
